/**
 * Custom display descriptions for every badge, keyed by snake_case `badge_id`.
 * These override whatever the API returns in `eligible_badges[].description`.
 * Note: `all_in_avenger` = "All-In Avenger" (API id), `giant_jump` = "Giant Jump".
 */
export const BADGE_DESCRIPTIONS: Record<string, string> = {
  // System / auto badges
  first_step: 'Your first Bhanzu class is done, with bright confidence today!',
  strong_start:
    'You have joined your first 5 sessions at Bhanzu, bright start!',
  streak_warrior:
    'You attended 5 sessions straight, a true Streak Warrior today!',
  streak_champion:
    'You joined 10 sessions straight, a true Streak Champion today!',
  streak_legend:
    'You attended 25 sessions straight, a true Streak Legend today!',
  unstoppable: 'You hit 50 sessions solid, truly unstoppable with focus today!',
  on_time_star:
    'You joined class on-time, ten times in a row like a Time Star!',
  giant_jump: 'You made a 75% score-jump with strong practice today, champ!',
  ace_learner: 'You scored high marks, showing strong Ace Learner focus today!',
  perfect_score:
    'You scored perfect 100%, showing sharp and steady focus today!',
  quiz_practice_star: '25 practice questions completed',
  quiz_practice_champion: '50 practice questions completed',
  quiz_practice_legend: '100 practice questions completed',
  quiz_module_attendance_star: 'Greater than 90% attendance in this module',
  quiz_mq_striker: '50%-74% accuracy',
  quiz_mq_star: '75%-90% accuracy',
  quiz_mq_master: '91%-99% accuracy',
  quiz_module_expert: '100% accuracy',
  quiz_champion: 'Weekly 80-90% accuracy in class quiz',
  quiz_legend: 'Weekly 90-100% accuracy in class quiz',
  quiz_go_getter: '50% improvement in quiz',
  quiz_big_leap: 'more than 75% improvement in quiz',
  // In-class (≤ 3rd grade)
  super_simba: 'You faced tougher questions bravely like Super Simba the lion!',
  kind_koala: 'You gave warm support, showing true Kind Koala kindness today!',
  einstein_spark:
    'You linked ideas fast with bright Einstein Spark energy today!',
  ryan_jr: 'You kept asking why, with wonderful Ryan Jr curiosity, always!',
  active_ninja:
    'You stayed active, alert, and responsive like a focused ninja!',
  gentle_giraffe:
    'You showed beautiful care and kindness with peers and trainer!',
  joyful_jupiter:
    'You gave smiles, cheer, and bright Joyful Jupiter vibes today!',
  brainy_bee: 'You solved each question with smart, buzzing Brainy Bee focus!',
  dolphin_memory:
    'Your recall was amazing, with true Dolphin Memory power today!',
  // In-class (4th+)
  iron_man: 'Like Iron-Man, you faced hard problems with brave focus today!',
  captain_kind:
    'You helped your teammates shine with true Captain Kind spirit!',
  flash_thinker:
    'You were lightning-fast and sharp, a real Flash Thinker moment!',
  curious_galileo:
    'You asked why, and made concepts click, Curious Galileo style!',
  all_in_avenger:
    'You met every challenge with true All-In Avenger power, today!',
  zen_master: 'You stayed calm, kind, focused, and balanced, like Zen Master!',
  joy_blaster: 'You filled the classroom with joy, energy, and cheerful focus!',
  curie_precision:
    'Your answers hit the mark with Curie Precision accuracy today!',
  memory_panther:
    'You recalled old lessons fast with Memory Panther focus today!',
  math_narrator:
    'You explained math nicely with calm Math Narrator focus today!',
  bounce_back_boss:
    'You bounced back from tests with Bounce-Back Boss grit, today!',
  // Special recognition (all grades)
  rising_star: 'You kept growing higher in your learning journey, Rising Star!',
  // English (Wordsmith)
  sound_hero: 'You found every sound hiding in that word. What a Sound Hero!',
  sound_scout: 'You spotted the sound so quickly. Your ears are super sharp!',
  book_boss:
    "You read the whole story on your own today. You're the Book Boss!",
  sentence_superstar:
    'I loved your full sentence answer. You sounded like a superstar speaker!',
  busy_bee: 'You were buzzing with ideas from start to finish today!',
  brave_bear:
    "You gave it a try even when it felt hard. That's what Brave Bears do!",
  listening_lion:
    'You listened carefully the very first time. What a Listening Lion!',
  clear_voice_captain:
    'Everyone could hear your wonderful voice today. Keep it up, Captain!',
  kind_heart:
    'You gave chance to your friend to speak first. You showed kindness.',
  fearless_falcon:
    "You didn't wait for the perfect answer—you bravely gave it a try.",
  reading_ranger:
    'You read carefully and accurately from beginning to end. Fantastic reading!',
  confidence_captain:
    'You spoke with courage and confidence today. Everyone was listening!',
  word_wizard:
    'You used some impressive words today. Your vocabulary is growing!',
  curious_cheetah:
    'Your question showed that you were really thinking deeply. Keep that alive!',
  power_participant:
    'You stayed involved from start to finish and made the class even better!',
  idea_inventor:
    'That was such an original idea! You made us think in a new way today.',
  rising_rocket:
    "I can see how much you've improved. You're moving higher every week!",
  word_warrior:
    "You didn't just learn today's word—you made it part of your conversation!",
  knowledge_contributor:
    "Today, you taught all of us something new. That's what great learners do!",
  expression_expert:
    'Your voice brought the text to life. It felt like the story was happening in front of us!',
  confidence_communicator:
    'You stood tall, spoke clearly, and owned your moment today.',
  discussion_builder:
    'You listened carefully and added value to the conversation.',
  curiosity_catalyst:
    'Your question pushed the discussion to the next level today.',
  homework_hero:
    'Your work showed effort, care, and commitment beyond the classroom.',
  resilience_rockstar:
    'You bounced back from challenges and kept moving forward.',
  reasoning_ranger:
    "You didn't stop at the answer, you explained your thinking too.",
  insight_investigator:
    'You looked beyond the words and understood the deeper meaning.',
  bridge_builder:
    "You connected today's learning to the real world in a meaningful way.",
  leadership_catalyst:
    'You helped the group move forward and brought out the best in others today.',
  perspective_shifter:
    'You challenged us to think about the topic from a completely different angle.',
  critical_thinker:
    'You backed up your ideas with logic and evidence. Well done!',
  question_architect:
    'Your question opened up an entirely new layer of discussion.',
  engagement_champion:
    "Your participation helped drive today's learning forward.",
  vocabulary_virtuoso:
    'Your word choices added depth and precision to the discussion.',
  solution_strategist:
    'You approached challenges thoughtfully and found effective solutions.',
};
